import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { Button, Text, NumberInput, LsaCategoriesModal, i18n, tokens, localizationMaps } from '@simplybenefits/sb-ui-library';
import * as dates from '@simplybenefits/sb-dates';
import { numberWithCommas } from "../../../services/helpers";
import { updateFormField } from '../../../actions/signup';
import Tip from "../../../components/Tip";

class SpendingAccounts extends PureComponent {
    state = {
        hsaAmount: this.props.initData.pooledAmountAvailable,
        lsaAmount: 0,
        amountAvailable: this.props.initData.pooledAmountAvailable,
        proratedHsa: 0,
        proratedLsa: 0,
        showLsaItemsModal: false
    };

    componentDidMount() {
        this.setState({
            hsaAmount: this.props.hsaAmount || this.props.initData.pooledAmountAvailable,
            lsaAmount: this.props.lsaAmount || 0
        });

        if (this.props.initData.isProrated) {
            this._setProratedAmounts();
        }
    }

    componentWillReceiveProps(nextProps) {
        this.setState({
            hsaAmount: nextProps.hsaAmount || this.props.initData.pooledAmountAvailable,
            lsaAmount: nextProps.lsaAmount || 0
        });
    }

    _onBlur = () => {
        this.props.dispatch(updateFormField('spendingAccounts', 'hsaAmount', this.state.hsaAmount));
        this.props.dispatch(updateFormField('spendingAccounts', 'lsaAmount', this.state.lsaAmount));
    };

    _handleHsaChange = async (value) => {
        // make the saved state to false, since we have made edits
        this.props.setSavedState('spendingAccounts', false);

        const amountAvailable = this.state.amountAvailable;
        const diff = amountAvailable - value;

        await this.setState({
            hsaAmount: value,
            lsaAmount: diff
        });

        if (this.props.initData.isProrated) {
            this._setProratedAmounts();
        }
    };

    _handleLsaChange = async (value) => {
        // make the saved state to false, since we have made edits
        this.props.setSavedState('spendingAccounts', false);

        const amountAvailable = this.state.amountAvailable;
        const diff = amountAvailable - value;

        await this.setState({
            lsaAmount: value,
            hsaAmount: diff
        });

        if (this.props.initData.isProrated) {
            this._setProratedAmounts();
        }
    };

    _validateForm = () => {
        let hsaAmountError = false;
        let lsaAmountError = false;

        const hsaAmount = Number(`${ this.state.hsaAmount }`.replace(',', ''));
        const lsaAmount = Number(`${ this.state.lsaAmount }`.replace(',', ''));

        if (hsaAmount + lsaAmount !== this.props.amountAvailable) {
            // eslint-disable-next-line no-multi-assign
            hsaAmountError = lsaAmountError = i18n.t(tokens.labels.spendingAccounts.ensureMaximumEqualsAvailable);
        }

        this.setState({
            hsaAmountError,
            lsaAmountError
        });

        if (
            hsaAmountError ||
            lsaAmountError
        ) {
            return false;
        }

        return true;
    };

    _next = () => {
        // make the saved state to true, we have hit next and everything is valid
        this.props.setSavedState('spendingAccounts', true);

        this.props.stepCompletion(
            {
                hsaAmount: this.state.hsaAmount,
                lsaAmount: this.state.lsaAmount
            },
            'spendingAccounts',
            false
        );
    };

    _setProratedAmounts = () => {
        const proratedBy = this.props.initData.proratedBy;
        const eligibilityDate = this.props.initData.eligibilityDate;

        const year = new Date(eligibilityDate * 1000).getUTCFullYear();
        const month = new Date(eligibilityDate * 1000).getUTCMonth() + 1;
        const day = new Date(eligibilityDate * 1000).getUTCDate();

        if (proratedBy === 'day') {
            const dailyHsaBalance = this.state.hsaAmount / 365;
            const dailyLsaBalance = this.state.lsaAmount / 365;

            const endOfYear = dates.getStartOfDay(`${ year + 1 }-01-01`);
            const timeLeftInYear = endOfYear - dates.getStartOfDay(eligibilityDate);
            let daysLeft = Math.floor(timeLeftInYear / 86400);

            // if it's the first of the year then use the full usage
            if (month === 1 && day === 1) {
                daysLeft = 365;
            }

            this.setState({
                proratedHsa: dailyHsaBalance * daysLeft,
                proratedLsa: dailyLsaBalance * daysLeft
            });
        } else if (proratedBy === 'month') {
            const monthlyHsaBalance = this.state.hsaAmount / 12;
            const monthlyLsaBalance = this.state.lsaAmount / 12;

            let monthsLeft = 12 - month;

            // if it's the first of the month then use the full usage for that month
            if (day === 1) {
                monthsLeft += 1;
            }

            this.setState({
                proratedHsa: monthlyHsaBalance * monthsLeft,
                proratedLsa: monthlyLsaBalance * monthsLeft
            });
        } else if (proratedBy === 'quarter') {
            const quarterHsaBalance = this.state.hsaAmount / 4;
            const quarterLsaBalance = this.state.lsaAmount / 4;

            const currentQuarter = Math.floor((new Date(eligibilityDate * 1000).getUTCMonth() + 3) / 3);
            let quartersLeft = 4 - currentQuarter;

            // if it's the first of jan, apr, july and oct then give them the full quarter
            if ([ 1, 4, 7, 10 ].indexOf(month) !== -1 && day === 1) {
                quartersLeft += 1;
            }

            if (quartersLeft > 4) {
                quartersLeft = 4;
            }

            this.setState({
                proratedHsa: quarterHsaBalance * quartersLeft,
                proratedLsa: quarterLsaBalance * quartersLeft
            });
        }
    };

    render() {
        const styles = {
            form: {
                display: 'flex',
                flexDirection: 'column',
                padding: "0 50px 0 50px"
            },
            stepperBtnContainer: {
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: '20px'
            },
            stepperNextBtn: {
                float: 'right',
                marginRight: '5px !important'
            }
        };

        return (
            <div>
                <Text largest bold margin="0 0 10px 0">{ i18n.t(tokens.titles.spendingAccounts) }</Text>

                <div style={styles.form}>
                    <Tip
                        left
                        show={ this.props.tips.spendingAccountBreakdown }
                        close={ () => this.props.hideTip('spendingAccountBreakdown') }
                        customStyle={{ marginTop: -90 }}
                    >
                        { i18n.t(tokens.labels.signup.thisSectionIsFor) } <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.signup.settingTheMaximums) }</span> { i18n.t(tokens.labels.signup.ofYourPooledSpendingAccounts)}.&nbsp;
                        { i18n.t(tokens.labels.signup.divideTotalAmountsBetweenHSAAndLSA) }.&nbsp;
                        <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.signup.thisCannotBeChangedLater)}.</span>
                    </Tip>

                    <Text small margin="0 0 10px 0">{ i18n.t(tokens.labels.spendingAccounts.pooledSpendingAccountNote) }</Text>

                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Text bold small margin="0 5px 0 0">{ i18n.t(tokens.labels.spendingAccounts.amountAvailable) } </Text>
                        <Text bold small>{ `$${ numberWithCommas(this.state.amountAvailable.toFixed(2)) }` }</Text>
                    </div>

                    <Tip
                        right
                        show={ this.props.tips.hsaBreakdown }
                        close={ () => this.props.hideTip('hsaBreakdown') }
                    >
                        { i18n.t(tokens.labels.signup.yourHealthSpendingAccountHSAIsA) } <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.coverage.nonTaxable).toLowerCase() }</span> { i18n.t(tokens.labels.signup.benefitItsUsedTo) }:&nbsp;
                        <li>{ i18n.t(tokens.labels.signup.topOffServiceLessThan100) }</li>
                        <li>{ i18n.t(tokens.labels.signup.coverCostsNotCoveredOnYourPlan) }</li>
                        <li>{ i18n.t(tokens.labels.signup.payForServicesThatHitMaximums) }</li>
                    </Tip>

                    <NumberInput
                        label={ i18n.t(tokens.labels.spendingAccounts.healthSpendingMaximum) }
                        prefix="$"
                        value={Number(this.state.hsaAmount)}
                        onBlur={this._onBlur}
                        onChange={(e) => this._handleHsaChange(e.target.value)}
                        error={ this.state.hsaAmountError }
                        fullWidth
                    />

                    <NumberInput
                        label={ i18n.t(tokens.labels.spendingAccounts.lifestyleSpendingMaximum) }
                        prefix="$ "
                        value={Number(this.state.lsaAmount)}
                        onBlur={this._onBlur}
                        onChange={(e) => this._handleLsaChange(e.target.value)}
                        error={ this.state.lsaAmountError }
                        fullWidth
                    />

                    {
                        this.props.initData.lsaItems &&
                            <div
                                onClick={() => this.setState({ showLsaItemsModal: true })}
                                style={{ cursor: 'pointer' }}
                            >
                                <Text tiny>{ i18n.t(tokens.labels.spendingAccounts.whatAmEligibleFor) } </Text>
                            </div>
                    }

                    {
                        this.state.showLsaItemsModal &&
                            <LsaCategoriesModal
                                open={ this.state.showLsaItemsModal }
                                items={ this.props.initData.lsaItems }
                                whitelabel={ this.props.whitelabel || {} }
                                close={ () => {
                                    this.setState({ showLsaItemsModal: false });
                                }}
                            />
                    }

                    {
                        this.props.initData.isProrated &&
                        <>
                            <Text small margin="10px 0 20px 0">
                                { i18n.t(tokens.labels.spendingAccounts.prorationPolicyNote, { proratedBy: localizationMaps.fromEnglish("proratingPeriod", this.props.initData.proratedBy) })}
                            </Text>

                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Text bold small margin="0 5px 0 0">{ i18n.t(tokens.labels.spendingAccounts.proratedHealthSpendingBalance) }</Text>
                                <Text bold small>{ `$${ numberWithCommas(Math.ceil(this.state.proratedHsa)) }` }</Text>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Text bold small margin="0 5px 0 0">{ i18n.t(tokens.labels.spendingAccounts.proratedLifestyleSpendingBalance) }</Text>
                                <Text bold small>{ `$${ numberWithCommas(Math.ceil(this.state.proratedLsa)) }` }</Text>
                            </div>
                        </>
                    }

                    {
                        this.props.initData.isQuarterlyAllowance &&
                            <Text small margin="10px 0 20px 0">
                                { i18n.t(tokens.labels.spendingAccounts.quarterlyAllowanceNote) }
                            </Text>
                    }

                    <Tip
                        left
                        show={ this.props.tips.lsaBreakdown }
                        close={ () => this.props.hideTip('lsaBreakdown') }
                        customStyle={{ marginTop: 150 }}
                    >
                        { i18n.t(tokens.labels.signup.yourLifestyleSpendingAccountLSAIsA) } <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.coverage.taxable).toLowerCase() }</span>&nbsp;
                        { i18n.t(tokens.labels.signup.benefitAndWillCountAs) } <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.signup.incomeEarned) }</span> { i18n.t(tokens.labels.signup.duringTaxSeason) }.&nbsp;
                        { i18n.t(tokens.labels.signup.youCanUseThisBenefitTo) } <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.signup.buyHealthGoodsAndServices) }</span>&nbsp;
                        { i18n.t(tokens.labels.signup.specifiedByEmployerNotIncludedInHealthPlan) }.
                    </Tip>

                    <div style={styles.stepperBtnContainer}>
                        <Button
                            customStyle={styles.stepperNextBtn}
                            onClick={this._next}
                            color="primary"
                            fullWidth
                        >
                            { i18n.t(tokens.buttons.continue) }
                        </Button>
                    </div>
                </div>
            </div>
        );
    }
}

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    hsaAmount: state.signup.spendingAccounts.hsaAmount,
    lsaAmount: state.signup.spendingAccounts.lsaAmount
});

export default connect(mapStateToProps)(SpendingAccounts);